import { getSystemConfig } from './systemConfig';

/** Ensures an origin string has a scheme (defaults to https) and no trailing slash. */
export function normalizeOrigin(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** postMessage type discriminators — shared contract with PatientAdminMUI. */
export const MSG = {
  HANDSHAKE:         'ADMIN_HANDSHAKE',
  HANDSHAKE_ACK:     'HANDSHAKE_ACK',
  PARAGRAPH_SELECTED:'PARAGRAPH_SELECTED',
  REFRESH_CONTENT:   'REFRESH_CONTENT',
} as const;

export type AdminMessage =
  | { type: typeof MSG.HANDSHAKE }
  | { type: typeof MSG.HANDSHAKE_ACK }
  | { type: typeof MSG.PARAGRAPH_SELECTED; code: string }
  | { type: typeof MSG.REFRESH_CONTENT };

/**
 * Returns the trusted admin portal origin from l_system_config (PatientAdminPortalURL).
 * Empty string / undefined means annotation mode is disabled for this deployment.
 */
export function getAdminOrigin(): string {
  return normalizeOrigin(getSystemConfig('PatientAdminPortalURL') ?? '');
}

/** Returns true if the page is running inside an admin iframe or was opened as an admin popup. */
export function isAdminFramed(): boolean {
  if (!getAdminOrigin()) return false;
  try {
    // Iframe: self !== top. Popup: opener is set.
    return window.self !== window.top || window.opener !== null;
  } catch {
    // Cross-origin window.top access throws — definitely in a cross-origin iframe.
    return true;
  }
}

/** Returns true if opened as a popup (has opener) rather than embedded as an iframe. */
export function isAdminPopup(): boolean {
  if (!getAdminOrigin()) return false;
  try {
    return window.self === window.top && window.opener !== null;
  } catch {
    return false;
  }
}

/** Send a typed message to the admin window (parent if iframe, opener if popup). No-ops if not in admin context. */
export function postToAdmin(msg: AdminMessage): void {
  const origin = getAdminOrigin();
  if (!origin) return;
  if (isAdminPopup()) {
    (window.opener as Window | null)?.postMessage(msg, origin);
  } else if (window.self !== window.top) {
    window.parent.postMessage(msg, origin);
  }
}
